/**
 * 班牌设备管理 store 模块
 * API: status 1=在线, 0=未绑定, -1=离线
 */
import {
  getDevices,
  getDeviceStatistics,
  getDevicesByStatus,
  getDevice,
  createDevice as apiCreate,
  updateDevice as apiUpdate,
  switchDeviceMode as apiSwitchMode,
  deleteDevice as apiDelete,
  rebootDevice,
  shutdownDevice,
  updateDeviceApp,
  uploadApkFile,
  setDevicePowerScheduleOnce,
  setDevicePowerScheduleWeekly,
  clearDevicePowerSchedule,
  batchRebootDevices,
  batchShutdownDevices,
  batchSetDevicePowerScheduleOnce,
  batchSetDevicePowerScheduleWeekly,
  batchClearDevicePowerSchedule,
  getDeviceOnlineStatus,
  getDeviceCommandHistory
} from '@/api/devices'

function normalizeDeviceKey(value) {
  return String(value ?? '').trim().toUpperCase()
}

function statusToFrontend(status) {
  if (status === 1 || status === '1') return 'online'
  if (status === 0 || status === '0') return 'unbound'
  if (status === -1 || status === '-1') return 'offline'
  return 'unbound'
}

function statusToApi(status) {
  if (status === 'online') return 1
  if (status === 'unbound') return 0
  if (status === 'offline') return -1
  return 0
}

function parseStyleConfig(rawStyles) {
  if (!rawStyles) return {}
  if (typeof rawStyles === 'object') return rawStyles
  if (typeof rawStyles !== 'string') return {}
  try {
    const parsed = JSON.parse(rawStyles)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (_) {
    return {}
  }
}

function normalizeModeInfo(rawModeInfo) {
  const source = rawModeInfo && typeof rawModeInfo === 'object' ? rawModeInfo : {}
  return {
    modeId: source.mode_id ?? source.modeId ?? null,
    typeId: source.type_id ?? source.typeId ?? null,
    typeName: source.type_name ?? source.typeName ?? '未设置模式'
  }
}

function normalizeBrand(value) {
  if (value === '' || value == null) return 0
  const normalized = Number(value)
  return Number.isNaN(normalized) ? 0 : normalized
}

function normalizeDevice(raw) {
  if (!raw) return null
  const deviceId = raw.device_id ?? raw.deviceId ?? raw.id ?? ''
  const styleConfig = parseStyleConfig(raw.styles)
  return {
    id: deviceId,
    deviceId,
    classId: raw.class_id ?? raw.classId ?? '',
    className: raw.class_name ?? raw.className ?? '',
    machineCode: raw.machine_code ?? raw.machineCode ?? '',
    lastIp: raw.last_ip ?? raw.lastIp ?? raw.device_ip ?? raw.deviceIp ?? '',
    deviceIp: raw.device_ip ?? raw.deviceIp ?? raw.last_ip ?? raw.lastIp ?? '',
    lastIpReportedAt: raw.last_ip_reported_at ?? raw.lastIpReportedAt ?? '',
    brand: normalizeBrand(raw.brand ?? raw.brand_desc),
    styles: raw.styles ?? '',
    styleConfig,
    template: styleConfig.template ?? raw.template ?? 'classic',
    status: statusToFrontend(raw.status),
    statusDesc: raw.status_desc ?? raw.statusDesc ?? '',
    lastSync: raw.last_sync ?? raw.lastSync ?? raw.update_time ?? raw.updateTime ?? '',
    lastSeen: raw.last_seen ?? raw.lastSeen ?? '',
    modeInfo: normalizeModeInfo(raw.mode_info ?? raw.modeInfo),
    createBy: raw.create_by ?? raw.createBy,
    createTime: raw.create_time ?? raw.createTime,
    updateBy: raw.update_by ?? raw.updateBy,
    updateTime: raw.update_time ?? raw.updateTime
  }
}

function normalizeDeviceStatus(raw, options = {}) {
  const {
    fallbackDeviceId = '',
    fallbackStatus = 'offline'
  } = options
  const online = !!(raw?.online ?? false)
  const actualDeviceId = raw?.actual_device_id ?? raw?.actualDeviceId ?? raw?.device_id ?? raw?.deviceId ?? fallbackDeviceId
  const status = fallbackStatus === 'unbound'
    ? 'unbound'
    : (online ? 'online' : 'offline')
  return {
    online,
    status,
    deviceId: raw?.device_id ?? raw?.deviceId ?? fallbackDeviceId,
    actualDeviceId,
    connectedDevices: Array.isArray(raw?.connected_devices)
      ? raw.connected_devices
      : (Array.isArray(raw?.connectedDevices) ? raw.connectedDevices : []),
    lastIp: raw?.last_ip ?? raw?.lastIp ?? raw?.device_ip ?? raw?.deviceIp ?? '',
    deviceIp: raw?.device_ip ?? raw?.deviceIp ?? raw?.last_ip ?? raw?.lastIp ?? '',
    lastIpReportedAt: raw?.last_ip_reported_at ?? raw?.lastIpReportedAt ?? '',
    lastSeen: raw?.last_seen ?? raw?.lastSeen ?? ''
  }
}

function normalizeCommandHistoryItem(raw) {
  if (!raw) return null
  return {
    id: raw.id ?? raw.msg_id ?? `${raw.device_id || ''}-${raw.sent_at || ''}`,
    msgId: raw.msg_id ?? raw.msgId ?? '',
    deviceId: raw.device_id ?? raw.deviceId ?? '',
    cmdType: raw.cmd_type ?? raw.cmdType ?? '',
    payload: raw.payload ?? {},
    status: raw.status ?? 'pending',
    ackSuccess: raw.ack_success ?? raw.ackSuccess ?? null,
    ackMessage: raw.ack_message ?? raw.ackMessage ?? '',
    ackResult: raw.ack_result ?? raw.ackResult ?? null,
    sentAt: raw.sent_at ?? raw.sentAt ?? '',
    ackedAt: raw.acked_at ?? raw.ackedAt ?? ''
  }
}

function resolveList(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.list)) return payload.list
  return []
}

function buildCommandKey(deviceId, action) {
  return `${normalizeDeviceKey(deviceId)}:${action}`
}

function normalizeDeviceIds(deviceIds = []) {
  return Array.from(new Set((Array.isArray(deviceIds) ? deviceIds : [])
    .map(item => String(item ?? '').trim())
    .filter(Boolean)))
}

function commitBatchLoading(commit, deviceIds, action, value) {
  normalizeDeviceIds(deviceIds).forEach(deviceId => {
    commit('SET_ACTION_LOADING', {
      key: buildCommandKey(deviceId, action),
      value
    })
  })
}

async function refreshBatchDevices(dispatch, deviceIds, options = {}) {
  const ids = normalizeDeviceIds(deviceIds)
  const { fetchStatus = false } = options
  await Promise.allSettled(ids.flatMap(deviceId => {
    const tasks = [
      dispatch('fetchCommandHistory', { deviceId, params: { limit: 10 } }).catch(() => [])
    ]
    if (fetchStatus) tasks.unshift(dispatch('fetchStatus', deviceId).catch(() => null))
    return tasks
  }))
}

export default {
  namespaced: true,
  state: {
    list: [],
    statistics: null,
    statusMap: {},
    commandHistoryMap: {},
    actionLoadingMap: {},
    loading: false,
    error: null
  },
  getters: {
    devices: state => state.list,
    statistics: state => state.statistics,
    loading: state => state.loading,
    error: state => state.error,
    getDeviceStatus: state => (deviceId) => state.statusMap[normalizeDeviceKey(deviceId)] ?? null,
    getCommandHistory: state => (deviceId) => state.commandHistoryMap[normalizeDeviceKey(deviceId)] ?? [],
    isActionLoading: state => (deviceId, action) => !!state.actionLoadingMap[buildCommandKey(deviceId, action)]
  },
  mutations: {
    SET_LIST(state, list) {
      state.list = (list || []).map(normalizeDevice).filter(Boolean)
    },
    SET_STATISTICS(state, data) {
      state.statistics = data
    },
    MERGE_DEVICE(state, partial) {
      const key = normalizeDeviceKey(partial?.deviceId ?? partial?.id)
      if (!key) return
      const index = state.list.findIndex(item => normalizeDeviceKey(item.deviceId ?? item.id) === key)
      if (index === -1) return
      state.list.splice(index, 1, {
        ...state.list[index],
        ...partial
      })
    },
    SET_DEVICE_STATUS(state, { deviceId, data }) {
      const key = normalizeDeviceKey(deviceId)
      if (!key) return
      state.statusMap = {
        ...state.statusMap,
        [key]: data
      }
    },
    SET_COMMAND_HISTORY(state, { deviceId, list }) {
      const key = normalizeDeviceKey(deviceId)
      if (!key) return
      state.commandHistoryMap = {
        ...state.commandHistoryMap,
        [key]: list
      }
    },
    SET_ACTION_LOADING(state, { key, value }) {
      state.actionLoadingMap = {
        ...state.actionLoadingMap,
        [key]: !!value
      }
    },
    SET_LOADING(state, v) {
      state.loading = !!v
    },
    SET_ERROR(state, err) {
      state.error = err
    }
  },
  actions: {
    async fetchList({ commit }, params = {}) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const res = params.status !== undefined
          ? await getDevicesByStatus(params.status)
          : await getDevices()
        const list = resolveList(res)
        commit('SET_LIST', list)
        return { list: (list || []).map(normalizeDevice).filter(Boolean) }
      } catch (e) {
        commit('SET_ERROR', e.message)
        throw e
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async fetchStatistics({ commit }) {
      try {
        const res = await getDeviceStatistics()
        commit('SET_STATISTICS', res.data ?? res)
        return res.data ?? res
      } catch (e) {
        commit('SET_STATISTICS', null)
        throw e
      }
    },
    async fetchOne(_, deviceId) {
      const res = await getDevice(deviceId)
      const data = res.data ?? res
      return normalizeDevice(data)
    },
    async fetchStatus({ commit, state }, deviceId) {
      const currentDevice = state.list.find((item) => normalizeDeviceKey(item.deviceId ?? item.id) === normalizeDeviceKey(deviceId))
      const res = await getDeviceOnlineStatus(deviceId)
      const normalized = normalizeDeviceStatus(res.data ?? res, {
        fallbackDeviceId: deviceId,
        fallbackStatus: currentDevice?.status || 'offline'
      })
      commit('SET_DEVICE_STATUS', { deviceId, data: normalized })
      commit('MERGE_DEVICE', {
        deviceId,
        status: normalized.status,
        lastSeen: normalized.lastSeen
      })
      return normalized
    },
    async fetchCommandHistory({ commit }, { deviceId, params = {} }) {
      const res = await getDeviceCommandHistory(deviceId, params)
      const list = resolveList(res).map(normalizeCommandHistoryItem).filter(Boolean)
      commit('SET_COMMAND_HISTORY', { deviceId, list })
      return list
    },
    async create({ rootState }, payload) {
      const data = {
        device_id: (payload.deviceId ?? payload.id ?? payload.device_id ?? '').toString().trim(),
        machine_code: (payload.machineCode ?? payload.machine_code ?? '').toString().trim(),
        brand: normalizeBrand(payload.brand),
        status: statusToApi(payload.status) ?? 0,
        create_by: rootState.currentUser?.name ?? rootState.currentUser?.username ?? 'admin'
      }
      if (payload.styles != null && payload.styles !== '') data.styles = payload.styles
      await apiCreate(data)
    },
    async update({ rootState }, { deviceId, data }) {
      const payload = {
        update_by: rootState.currentUser?.name ?? rootState.currentUser?.username ?? 'admin'
      }
      if (data.machineCode != null) payload.machine_code = data.machineCode
      if (data.brand != null) payload.brand = normalizeBrand(data.brand)
      if (data.styles != null) payload.styles = data.styles
      if (data.status != null) payload.status = statusToApi(data.status)
      await apiUpdate(deviceId, payload)
    },
    async switchMode({ commit }, { deviceId, typeId }) {
      const normalizedTypeId = Number(typeId)
      if (!Number.isInteger(normalizedTypeId) || normalizedTypeId <= 0) {
        const err = new Error('模式类型无效')
        err.status = 400
        throw err
      }

      const key = buildCommandKey(deviceId, 'switch-mode')
      commit('SET_ACTION_LOADING', { key, value: true })
      try {
        const res = await apiSwitchMode(deviceId, { type_id: normalizedTypeId })
        const payload = res.data ?? res
        const modeInfo = normalizeModeInfo(payload?.mode_info ?? payload?.modeInfo)
        commit('MERGE_DEVICE', {
          deviceId,
          modeInfo,
          updateTime: payload?.update_time ?? payload?.updateTime
        })
        return payload
      } finally {
        commit('SET_ACTION_LOADING', { key, value: false })
      }
    },
    async remove(_, deviceId) {
      await apiDelete(deviceId)
    },
    async reboot({ commit, dispatch }, deviceId) {
      const key = buildCommandKey(deviceId, 'reboot')
      commit('SET_ACTION_LOADING', { key, value: true })
      try {
        const res = await rebootDevice(deviceId)
        await Promise.allSettled([
          dispatch('fetchStatus', deviceId),
          dispatch('fetchCommandHistory', { deviceId, params: { limit: 10 } })
        ])
        return res
      } finally {
        commit('SET_ACTION_LOADING', { key, value: false })
      }
    },
    async shutdown({ commit, dispatch }, deviceId) {
      const key = buildCommandKey(deviceId, 'shutdown')
      commit('SET_ACTION_LOADING', { key, value: true })
      try {
        const res = await shutdownDevice(deviceId)
        await Promise.allSettled([
          dispatch('fetchStatus', deviceId),
          dispatch('fetchCommandHistory', { deviceId, params: { limit: 10 } })
        ])
        return res
      } finally {
        commit('SET_ACTION_LOADING', { key, value: false })
      }
    },
    async appUpdate({ commit, dispatch }, { deviceId, data }) {
      const key = buildCommandKey(deviceId, 'app-update')
      commit('SET_ACTION_LOADING', { key, value: true })

      try {
        const res = await updateDeviceApp(deviceId, data)

        await Promise.allSettled([
          dispatch('fetchStatus', deviceId),
          dispatch('fetchCommandHistory', {
            deviceId,
            params: {
              limit: 10,
              cmd_type: 'server_app_update'
            }
          })
        ])

        return res
      } finally {
        commit('SET_ACTION_LOADING', { key, value: false })
      }
    },
    async uploadApk(_, file) {
      return uploadApkFile(file)
    },
    async scheduleOnce({ commit, dispatch }, payload) {
      const key = buildCommandKey(payload.deviceId, 'schedule-once')
      commit('SET_ACTION_LOADING', { key, value: true })
      try {
        const res = await setDevicePowerScheduleOnce(payload.deviceId, payload.data)
        await dispatch('fetchCommandHistory', { deviceId: payload.deviceId, params: { limit: 10 } }).catch(() => [])
        return res
      } finally {
        commit('SET_ACTION_LOADING', { key, value: false })
      }
    },
    async scheduleWeekly({ commit, dispatch }, payload) {
      const key = buildCommandKey(payload.deviceId, 'schedule-weekly')
      commit('SET_ACTION_LOADING', { key, value: true })
      try {
        const res = await setDevicePowerScheduleWeekly(payload.deviceId, payload.data)
        await dispatch('fetchCommandHistory', { deviceId: payload.deviceId, params: { limit: 10 } }).catch(() => [])
        return res
      } finally {
        commit('SET_ACTION_LOADING', { key, value: false })
      }
    },
    async clearPowerSchedule({ commit, dispatch }, deviceId) {
      const key = buildCommandKey(deviceId, 'schedule-clear')
      commit('SET_ACTION_LOADING', { key, value: true })
      try {
        const res = await clearDevicePowerSchedule(deviceId)
        await dispatch('fetchCommandHistory', { deviceId, params: { limit: 10 } }).catch(() => [])
        return res
      } finally {
        commit('SET_ACTION_LOADING', { key, value: false })
      }
    },
    async batchReboot({ commit, dispatch }, deviceIds) {
      const ids = normalizeDeviceIds(deviceIds)
      commitBatchLoading(commit, ids, 'reboot', true)
      try {
        const res = await batchRebootDevices(ids)
        await refreshBatchDevices(dispatch, ids, { fetchStatus: true })
        return res
      } finally {
        commitBatchLoading(commit, ids, 'reboot', false)
      }
    },
    async batchShutdown({ commit, dispatch }, deviceIds) {
      const ids = normalizeDeviceIds(deviceIds)
      commitBatchLoading(commit, ids, 'shutdown', true)
      try {
        const res = await batchShutdownDevices(ids)
        await refreshBatchDevices(dispatch, ids, { fetchStatus: true })
        return res
      } finally {
        commitBatchLoading(commit, ids, 'shutdown', false)
      }
    },
    async batchAppUpdate({ commit, dispatch }, payload) {
      const ids = normalizeDeviceIds(payload?.deviceIds)
      const data = payload?.data || {}
      commitBatchLoading(commit, ids, 'app-update', true)
      try {
        const results = await Promise.allSettled(ids.map(deviceId => updateDeviceApp(deviceId, data)))
        const failed = results.filter(result => result.status === 'rejected')

        await Promise.allSettled(ids.flatMap(deviceId => [
          dispatch('fetchStatus', deviceId).catch(() => null),
          dispatch('fetchCommandHistory', {
            deviceId,
            params: {
              limit: 10,
              cmd_type: 'server_app_update'
            }
          }).catch(() => [])
        ]))

        if (failed.length) {
          const firstError = failed[0].reason
          throw new Error(firstError?.message || `软件更新命令下发失败 ${failed.length} 台`)
        }

        const firstResolved = results.find(result => result.status === 'fulfilled')?.value
        return firstResolved || { message: '软件更新命令已下发' }
      } finally {
        commitBatchLoading(commit, ids, 'app-update', false)
      }
    },
    async batchScheduleOnce({ commit, dispatch }, payload) {
      const ids = normalizeDeviceIds(payload.deviceIds)
      commitBatchLoading(commit, ids, 'schedule-once', true)
      try {
        const res = await batchSetDevicePowerScheduleOnce({
          device_ids: ids,
          ...payload.data
        })
        await refreshBatchDevices(dispatch, ids)
        return res
      } finally {
        commitBatchLoading(commit, ids, 'schedule-once', false)
      }
    },
    async batchScheduleWeekly({ commit, dispatch }, payload) {
      const ids = normalizeDeviceIds(payload.deviceIds)
      commitBatchLoading(commit, ids, 'schedule-weekly', true)
      try {
        const res = await batchSetDevicePowerScheduleWeekly({
          device_ids: ids,
          ...payload.data
        })
        await refreshBatchDevices(dispatch, ids)
        return res
      } finally {
        commitBatchLoading(commit, ids, 'schedule-weekly', false)
      }
    },
    async batchClearPowerSchedule({ commit, dispatch }, deviceIds) {
      const ids = normalizeDeviceIds(deviceIds)
      commitBatchLoading(commit, ids, 'schedule-clear', true)
      try {
        const res = await batchClearDevicePowerSchedule(ids)
        await refreshBatchDevices(dispatch, ids)
        return res
      } finally {
        commitBatchLoading(commit, ids, 'schedule-clear', false)
      }
    }
  }
}
